import { Editor, isTextSelection } from "@tiptap/core";
import { EditorState, Plugin, PluginKey } from "@tiptap/pm/state";
import { EditorView } from "@tiptap/pm/view";
import tippy, { Instance, Props } from "tippy.js";

import { getElementsForGroup } from "@/editor/utils/nodes";
import { doesSupportComposedRangeApi, getPositionFromRange, getSelection } from "@/editor/utils/selection";
import { Store } from "@/store";

import { HighlightInteractionEvent, isHighlightInteractedEvent } from "../highlight/events";

export interface BubbleMenuPluginProps {
  /**
   * The plugin key.
   * @type {PluginKey | string}
   * @default 'bubbleMenu'
   */
  pluginKey: PluginKey | string;

  /**
   * The editor instance.
   */
  editor: Editor;

  /**
   * The DOM element that contains your menu.
   * @type {HTMLElement}
   * @default null
   */
  element: HTMLElement;

  /**
   * The options for the tippy.js instance.
   * @see https://atomiks.github.io/tippyjs/v6/all-props/
   */
  tippyOptions?: Partial<Props>;

  /**
   * A function that returns whether the editor has an active highlight.
   */
  hasActiveHighlight: () => boolean;
}

export type BubbleMenuViewProps = BubbleMenuPluginProps & {
  view: EditorView;
};

export class BubbleMenuView {
  public editor: Editor;
  public element: HTMLElement;
  public view: EditorView;
  public tippy: Instance | undefined;
  public tippyOptions?: Partial<Props>;
  public preventHide = false;
  private isOpen: boolean = false;
  private selectionDebounceTimeout: number | null = null;

  private readonly updateDelay: number = 150;
  public readonly hasActiveHighlight: () => boolean;

  constructor({ editor, element, view, hasActiveHighlight, tippyOptions = {} }: BubbleMenuViewProps) {
    this.hasActiveHighlight = hasActiveHighlight;
    this.editor = editor;
    this.element = element;
    this.view = view;

    if (doesSupportComposedRangeApi(window)) {
      document.addEventListener("selectionchange", this.safariSelectionChangeHandler);
    }

    addEventListener("mousedown", this.documentMousedownHandler, { passive: true });
    this.element.addEventListener("mousedown", this.mousedownHandler, { capture: true });
    this.view.dom.addEventListener("dragstart", this.dragstartHandler);
    this.tippyOptions = tippyOptions;
    // Detaches menu content from its current parent
    this.element.remove();
    this.element.style.visibility = "visible";
    this.element.addEventListener("viewchange", this.handleViewChange, { capture: true });

    this.view.dom.addEventListener(
      HighlightInteractionEvent.HIGHLIGHT_INTERACTED,
      this.handleHighlightInteracted as EventListener,
    );

    this.view.dom.addEventListener(HighlightInteractionEvent.CLOSE_BUBBLE_MENU, this.handleCloseBubbleMenu);
  }

  private mousedownHandler = (event: MouseEvent) => {
    this.preventHide = true;
    if (doesSupportComposedRangeApi(window)) {
      // Prevent default only in Safari to maintain selection
      event.preventDefault();
    }
  };

  private safariSelectionChangeHandler = () => {
    setTimeout(() => {
      this.editor.commands.setTextSelection(this.editor.state.selection);
    }, 0);
  };

  private documentMousedownHandler = (event: MouseEvent) => {
    if (!this.isOpen) {
      return;
    }

    const path = event.composedPath() as Node[];
    const isOutside = !path.includes(this.element);

    if (isOutside) {
      this.hide();
    }
  };

  private dragstartHandler = () => {
    this.hide();
  };

  private blurHandler = ({ event }: { event: FocusEvent }) => {
    if (this.preventHide) {
      this.preventHide = false;
      return;
    }

    if (event?.relatedTarget && this.element.parentNode?.contains(event.relatedTarget as Node)) {
      return;
    }

    this.hide();
  };

  private tippyBlurHandler = (event: FocusEvent) => {
    this.blurHandler({ event });
  };

  private createTooltip = () => {
    const { element: editorElement } = this.editor.options;

    if (this.tippy) {
      return;
    }

    this.tippy = tippy(editorElement, {
      delay: 0,
      content: this.element,
      trigger: "manual",
      placement: "top",
      hideOnClick: "toggle",
      maxWidth: "none",
      ...this.tippyOptions,
      animation: "scale-subtle",
      allowHTML: true,
      sticky: "reference",
      // Prevent the tooltip from taking focus
      interactive: true,
      interactiveDebounce: 0,
      interactiveBorder: 0,
    });

    if (this.tippy.popper.firstChild) {
      (this.tippy.popper.firstChild as HTMLElement).addEventListener("blur", this.tippyBlurHandler);
    }
  };

  private show = () => {
    this.isOpen = true;
    this.tippy?.show();
    Store.editor.setToolbarActive(true);
  };

  private hide = () => {
    this.tippy?.hide();
    Store.editor.setToolbarActive(false);
  };

  update(view: EditorView, oldState?: EditorState) {
    const selection = getSelection(this.editor, view);
    if (selection == null) {
      return;
    }

    const hasValidSelection = selection.from !== selection.to;

    if (hasValidSelection) {
      this.handleDebouncedUpdate(view, oldState);
      return;
    }

    const selectionChanged = !oldState?.selection.eq(view.state.selection);
    const docChanged = !oldState?.doc.eq(view.state.doc);

    this.updateHandler(view, selectionChanged, docChanged, oldState);
  }

  private handleDebouncedUpdate = (view: EditorView, oldState?: EditorState) => {
    const { from, to } = getPositionFromRange(view.state.selection);
    const selectionChanged = from !== to;
    const docChanged = !oldState?.doc.eq(view.state.doc);

    if (!selectionChanged && !docChanged) {
      return;
    }

    if (this.selectionDebounceTimeout) {
      clearTimeout(this.selectionDebounceTimeout);
    }

    this.selectionDebounceTimeout = window.setTimeout(() => {
      this.updateHandler(view, selectionChanged, docChanged, oldState);
    }, this.updateDelay);
  };

  private updateHandler = (
    view: EditorView,
    selectionChanged: boolean,
    docChanged: boolean,
    oldState?: EditorState,
  ) => {
    const { state, composing } = view;
    const isSame = !selectionChanged && !docChanged;

    if (composing || isSame) {
      return;
    }

    const selection = getSelection(this.editor, view);

    if (selection == null) {
      return;
    }

    const { ranges } = selection;
    const from = Math.min(...ranges.map(range => range.$from.pos));
    const to = Math.max(...ranges.map(range => range.$to.pos));

    const shouldShow = this.shouldShow?.({
      editor: this.editor,
      element: this.element,
      view,
      state,
      oldState,
      from,
      to,
    });

    if (!shouldShow) {
      this.hide();
      return;
    }

    this.createTooltip();

    this.tippy?.setProps({
      getReferenceClientRect: this.hasActiveHighlight()
        ? () => this.getClientRectFromGroup(Store.editor.activeHighlightGroup.value!)
        : this.getClientRectFromSelection,
    });

    this.show();
  };

  private handleViewChange = () => {
    this.tippy?.popperInstance?.update();
  };

  private shouldShow = (props: {
    editor: Editor;
    element: HTMLElement;
    view: EditorView;
    state: EditorState;
    oldState?: EditorState;
    from: number;
    to: number;
  }) => {
    const { state } = props;
    const selection = getSelection(props.editor, props.view);

    if (selection == null) {
      return false;
    }

    const selectedText = state.doc.textBetween(props.from, props.to).trim();
    const selectionLength = selectedText.length;
    return (!selection.empty && isTextSelection(selection) && selectionLength > 2) || this.hasActiveHighlight();
  };

  private handleCloseBubbleMenu = () => {
    this.tippy?.setProps({
      getReferenceClientRect: undefined,
    });

    this.hide();
  };

  private handleHighlightInteracted = (event: Event) => {
    if (!isHighlightInteractedEvent(event)) {
      return;
    }

    const group = Store.editor.activeHighlightGroup.value;
    if (group == null) {
      return;
    }

    if (this.isOpen) {
      this.hide();
    }

    this.preventHide = true;

    this.createTooltip();

    this.tippy?.setProps({
      getReferenceClientRect: (): DOMRect => {
        const rect = this.getClientRectFromGroup(group!);
        return new DOMRect(rect.left, rect.top, rect.width, rect.height);
      },
    });

    this.show();

    setTimeout(() => {
      this.preventHide = false;
    }, 100);
  };

  destroy = () => {
    if (this.tippy?.popper.firstChild) {
      (this.tippy.popper.firstChild as HTMLElement).removeEventListener("blur", this.tippyBlurHandler);
    }

    this.tippy?.destroy();
    removeEventListener("mousedown", this.documentMousedownHandler);
    this.element.removeEventListener("mousedown", this.mousedownHandler, { capture: true });
    this.view.dom.removeEventListener("dragstart", this.dragstartHandler);
    this.view.dom.removeEventListener(
      HighlightInteractionEvent.HIGHLIGHT_INTERACTED,
      this.handleHighlightInteracted as EventListener,
    );
    this.view.dom.removeEventListener(HighlightInteractionEvent.CLOSE_BUBBLE_MENU, this.handleCloseBubbleMenu);
    this.editor.off("blur", this.blurHandler);

    if (doesSupportComposedRangeApi(window)) {
      document.removeEventListener("selectionchange", this.safariSelectionChangeHandler);
    }

    if (this.selectionDebounceTimeout) {
      window.clearTimeout(this.selectionDebounceTimeout);
    }
  };

  private getClientRectFromGroup = (group: string): DOMRect => {
    const nodes = getElementsForGroup(this.view, group, "nw-highlight");
    if (nodes.length === 1) {
      return nodes[0].getBoundingClientRect();
    }

    const boundingRect = nodes.reduce(
      (acc, node) => {
        const rect = node.getBoundingClientRect();
        return {
          top: Math.min(acc.top, rect.top),
          left: Math.min(acc.left, rect.left),
          right: Math.max(acc.right, rect.right),
          bottom: Math.max(acc.bottom, rect.bottom),
        };
      },
      { top: Infinity, left: Infinity, right: -Infinity, bottom: -Infinity },
    );
    return new DOMRect(
      boundingRect.left,
      boundingRect.top,
      boundingRect.right - boundingRect.left,
      boundingRect.bottom - boundingRect.top,
    );
  };

  private getClientRectFromSelection = (): DOMRect => {
    const selection = getSelection(this.editor, this.view);

    if (selection == null) {
      return new DOMRect(0, 0, 0, 0);
    }

    const { from, to } = selection;
    const start = this.view.coordsAtPos(from);
    const end = this.view.coordsAtPos(to);
    const res = new DOMRect(
      Math.min(start.left, end.left),
      Math.min(start.top, end.top),
      Math.max(start.right, end.right) - Math.min(start.left, end.left),
      Math.max(start.bottom, end.bottom) - Math.min(start.top, end.top),
    );

    return res;
  };
}

export const BubbleMenuPlugin = (options: BubbleMenuPluginProps) => {
  return new Plugin({
    key: typeof options.pluginKey === "string" ? new PluginKey(options.pluginKey) : options.pluginKey,
    view: view => new BubbleMenuView({ view, ...options }),
  });
};
